import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById } from '@/mocks/comics';
import { getReviewsByComicId } from '@/mocks/reviews';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架。</p>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const comicReviews = getReviewsByComicId(comic.id);

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `http://qjmd.cn/comic/${comic.id}`,
    image: comic.cover,
    genre: comic.category,
    datePublished: '2018-01-01',
    dateModified: comic.updateTime,
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是免费的吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，《${comic.title}》在漫画皮平台免费提供在线阅读。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}多久更新一次？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》目前${comic.status}，最新更新至第${comic.latestChapter}话，更新频率视作者进度而定。`,
        },
      },
    ],
  };

  const chapters = Array.from({ length: Math.min(comic.latestChapter, 20) }, (_, i) => ({
    num: comic.latestChapter - i,
    title: `第${comic.latestChapter - i}话`,
    time: new Date(new Date(comic.updateTime).getTime() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '全部漫画', href: '/category/all' },
                { label: comic.title },
              ]}
            />

            {/* Comic Info */}
            <section className="py-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 w-full md:w-[280px]">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 漫画皮在线漫画`}
                      title={`${comic.title} - ${comic.author}作品`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                      作品档案
                    </span>
                    {comic.status === '连载中' ? (
                      <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                        连载中
                      </span>
                    ) : (
                      <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                        已完结
                      </span>
                    )}
                  </div>

                  <h1 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-foreground-500 mb-4">作者：{comic.author}</p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {comic.category.map((cat) => (
                      <span key={cat} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                        {cat}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-6 mb-5 text-sm">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="font-semibold text-foreground-950">{comic.rating}</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-eye-line"></i>
                      <span>{(comic.hotness / 10000).toFixed(0)}万人气</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-file-list-3-line"></i>
                      <span>共{comic.totalChapters}话</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-calendar-line"></i>
                      <span>更新于 {comic.updateTime}</span>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 mb-6">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
                    >
                      开始阅读
                      <i className="ri-play-line ml-1.5"></i>
                    </Link>
                    <button className="px-8 py-3 border border-background-300/60 text-foreground-700 rounded-full text-sm font-medium hover:border-accent-500 hover:text-accent-500 transition-all whitespace-nowrap">
                      <i className="ri-bookmark-line mr-1.5"></i>
                      加入书架
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Chapter List */}
            <section className="py-10 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {chapters.map((ch) => (
                  <Link
                    key={ch.num}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-accent-500 transition-colors whitespace-nowrap">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.time}</span>
                  </Link>
                ))}
              </div>
              {comic.latestChapter > 20 && (
                <div className="text-center mt-6">
                  <button className="px-6 py-2.5 border border-background-200/70 text-foreground-600 rounded-full text-sm hover:border-accent-500 hover:text-accent-500 transition-all whitespace-nowrap">
                    加载更多章节
                    <i className="ri-arrow-down-s-line ml-1"></i>
                  </button>
                </div>
              )}
            </section>

            {/* Reviews */}
            {comicReviews.length > 0 && (
              <section className="py-10 border-t border-background-200/70">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">
                  读者<span className="text-accent-500">书评</span>
                  <span className="ml-2 text-sm font-normal text-foreground-400">({comicReviews.length})</span>
                </h2>
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
                  {comicReviews.map((review) => (
                    <div key={review.id} className="break-inside-avoid mb-5 bg-background-100 rounded-lg p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={review.avatar}
                          alt={review.userName}
                          className="w-10 h-10 rounded-full object-cover"
                          loading="lazy"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{review.userName}</div>
                          <div className="text-xs text-foreground-400">{review.time}</div>
                        </div>
                        <div className="ml-auto flex items-center gap-0.5 text-accent-500">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-xs"></i>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        <i className="ri-double-quotes-l text-accent-300 mr-1"></i>
                        {review.content}
                        <i className="ri-double-quotes-r text-accent-300 ml-1"></i>
                      </p>
                      <div className="flex items-center gap-1 mt-3 text-xs text-foreground-400">
                        <i className="ri-thumb-up-line"></i>
                        <span>{review.likes}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}